import { useEffect, useMemo, useState } from 'react';
import {
  BOT_ARCHETYPES,
  WHEEL_PHASE_META,
  type WheelObservableState,
  type WheelSeatView,
  type WheelSubSpinKind,
  type WheelSubSpinResult,
} from '@goodtime/shared';
import { SeatAvatar, resolveSeatName, useSeatName } from './SeatAvatar';
import { usePlayerName } from '../../avatar/usePlayerName';
import { Icon } from '../Icon';

const SUB_SPIN_LABEL: Record<WheelSubSpinKind, string> = {
  FLOOR: 'Floor pay',
  PRIZE: 'Prize spin',
  JACKPOT: 'Jackpot spin',
};

const SUB_SPIN_ACCENT: Record<WheelSubSpinKind, string> = {
  FLOOR: '#9DE54E',
  PRIZE: '#FFC940',
  JACKPOT: '#FF6F6F',
};

export interface WheelResolutionViewProps {
  state: WheelObservableState;
  selfSeatId?: number | null;
  /** F2.2.next — onboarding curriculum step (1..5). When < 4, hides
   *  the JACKPOT sub-spin tile so sessions 1–3 only see Floor + Prize
   *  resolves. Per UX §4.4, the jackpot pillar unlocks once the
   *  player has crossed the 50-token jackpot-eligibility threshold —
   *  showing the empty pillar before then teases reward the player
   *  can't yet claim. Null / undefined disables gating. */
  onboardingSession?: number | null;
}

/**
 * U6 — Wheel resolution screen. Renders during the WHEEL phase. The
 * server fires three WHEEL_TICK commands on a steady cadence (Floor →
 * Prize → Jackpot); each tick lands a slice and emits a
 * {@link WheelSubSpinResult} that arrives via SSE delta. We render
 * the wheel as a circular plate of seat slices, animate the active
 * pointer between sub-spins, and pop a payout chip beside the
 * landing seat as each tick resolves.
 *
 * <p>Visual fidelity bar is "ugly but functional" per the master
 * plan: the wheel is a CSS conic-gradient + an arrow indicator;
 * scattered avatars sit at the slice centres. Polish for the
 * spinning Pixi-quality wheel ships once the slot-canvas pattern
 * is repurposed in M3 polish.
 */
export function WheelResolutionView({
  state,
  selfSeatId = null,
  onboardingSession = null,
}: WheelResolutionViewProps) {
  const phaseMeta = WHEEL_PHASE_META.WHEEL;
  const seats = state.seats;

  // F2.2.next — jackpot unlock at session 4 (UX §4.4). Earlier
  // sessions render only the Floor + Prize pillars; the JACKPOT
  // tile is hidden so the player isn't teased with a reward they
  // can't claim until the curriculum opens it up.
  const showJackpot = (onboardingSession ?? 5) >= 4;
  const subSpins: WheelSubSpinKind[] = showJackpot
    ? ['FLOOR', 'PRIZE', 'JACKPOT']
    : ['FLOOR', 'PRIZE'];
  const resultsByKind = useMemo(() => {
    const m = new Map<WheelSubSpinKind, WheelSubSpinResult>();
    for (const r of state.subSpinResults) m.set(r.subSpin, r);
    return m;
  }, [state.subSpinResults]);

  const completedCount = state.subSpinResults.length;
  const currentKind: WheelSubSpinKind | null =
    completedCount < subSpins.length ? subSpins[completedCount] ?? null : null;
  const currentAccent = currentKind ? SUB_SPIN_ACCENT[currentKind] : '#FFFFFF';

  // ---- payout-chip animation ------------------------------------------
  // When a new sub-spin result lands, pop a payout chip beside the
  // winning seat for a couple of seconds. Tracks results by sub-spin
  // kind so a re-render with the same data doesn't re-spawn chips.
  const [poppedKinds, setPoppedKinds] = useState<Set<WheelSubSpinKind>>(new Set());
  useEffect(() => {
    for (const r of state.subSpinResults) {
      if (poppedKinds.has(r.subSpin)) continue;
      setPoppedKinds((prev) => new Set([...prev, r.subSpin]));
    }
  }, [state.subSpinResults, poppedKinds]);

  // Total payouts per seat across all completed sub-spins, for the
  // mid-wheel summary chip.
  const seatTotals = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of state.subSpinResults) {
      for (const [seatIdStr, coins] of Object.entries(r.payouts)) {
        const id = Number(seatIdStr);
        m.set(id, (m.get(id) ?? 0) + (coins as number));
      }
    }
    return m;
  }, [state.subSpinResults]);

  // ---- render ----------------------------------------------------------
  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        background:
          'radial-gradient(ellipse at top, #4A1230 0%, #20081A 60%, #060d22 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '12px 8px 4px' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: currentKind ? currentAccent : phaseMeta.accent,
          }}
        >
          ⚡ {currentKind ? SUB_SPIN_LABEL[currentKind] : 'Spinning the wheel…'}
        </div>
        <div style={{ fontSize: 11, opacity: 0.6, marginTop: 4 }}>
          {completedCount} of 3 sub-spins resolved
        </div>
      </div>

      {/* Sub-spin progress chips */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 10 }}>
        {subSpins.map((kind, idx) => {
          const result = resultsByKind.get(kind);
          const isCurrent = idx === completedCount;
          const isDone = !!result;
          return (
            <SubSpinChip
              key={kind}
              kind={kind}
              isCurrent={isCurrent}
              isDone={isDone}
              winner={isDone ? findSeat(seats, result.landedSliceOwner) : null}
            />
          );
        })}
      </div>

      {/* Wheel + scattered seats */}
      <WheelPlate
        seats={seats}
        currentKind={currentKind}
        completedCount={completedCount}
      />

      {/* Per-seat totals — stacked at the bottom for visibility */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '0 12px',
        }}
      >
        {seats.map((seat) => {
          const total = seatTotals.get(seat.seatId) ?? 0;
          const isSelf = selfSeatId !== null && seat.seatId === selfSeatId;
          return (
            <PayoutTile
              key={seat.seatId}
              seat={seat}
              total={total}
              highlighted={isSelf}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---- subcomponents --------------------------------------------------------

function SubSpinChip({
  kind,
  isCurrent,
  isDone,
  winner,
}: {
  kind: WheelSubSpinKind;
  isCurrent: boolean;
  isDone: boolean;
  winner: WheelSeatView | null;
}) {
  const accent = SUB_SPIN_ACCENT[kind];
  const [localName] = usePlayerName();
  const winnerName = winner ? resolveSeatName(winner, localName) : null;
  return (
    <div
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        background: isDone
          ? `${accent}22`
          : isCurrent
            ? `${accent}55`
            : 'rgba(255,255,255,0.04)',
        border: `1px solid ${isDone || isCurrent ? `${accent}aa` : 'rgba(255,255,255,0.08)'}`,
        fontSize: 11,
        fontWeight: 700,
        color: isDone || isCurrent ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 110,
        justifyContent: 'center',
      }}
    >
      <span>{isCurrent ? `${SUB_SPIN_LABEL[kind]}…` : SUB_SPIN_LABEL[kind]}</span>
      {isDone && winner && winnerName ? (
        <>
          <span style={{ opacity: 0.5 }}>·</span>
          <span style={{ color: accent, fontWeight: 800 }}>
            {winnerName.split(' ')[0]}
          </span>
        </>
      ) : null}
    </div>
  );
}

function WheelPlate({
  seats,
  currentKind,
  completedCount,
}: {
  seats: ReadonlyArray<WheelSeatView>;
  currentKind: WheelSubSpinKind | null;
  completedCount: number;
}) {
  const wheelSize = 360;
  const seatRadius = wheelSize / 2 - 18;
  const accent = currentKind ? SUB_SPIN_ACCENT[currentKind] : '#FFC940';

  const conicSegments = useMemo(() => {
    // Even slice per seat, coloured by archetype/PLAYER.
    const segs: string[] = [];
    const sliceDeg = 360 / seats.length;
    seats.forEach((seat, i) => {
      const colour =
        seat.kind === 'BOT' && seat.archetype
          ? BOT_ARCHETYPES[seat.archetype].accent
          : '#5C6BC0';
      const start = i * sliceDeg;
      const end = (i + 1) * sliceDeg;
      segs.push(`${colour}cc ${start}deg ${end}deg`);
    });
    return segs.join(', ');
  }, [seats]);

  return (
    <div
      style={{
        position: 'relative',
        width: wheelSize + 80,
        height: wheelSize + 80,
        margin: '24px auto 0',
      }}
    >
      {/* Wheel disk */}
      <div
        style={{
          position: 'absolute',
          left: 40,
          top: 40,
          width: wheelSize,
          height: wheelSize,
          borderRadius: '50%',
          background: `conic-gradient(${conicSegments})`,
          boxShadow: `0 0 0 8px rgba(0,0,0,0.45), 0 0 0 9px ${accent}66, 0 14px 32px rgba(0,0,0,0.6)`,
          transition: 'box-shadow 400ms ease-out',
        }}
      />
      {/* Slice dividers + seat labels */}
      {seats.map((seat, i) => {
        const sliceDeg = 360 / seats.length;
        const angleDeg = i * sliceDeg + sliceDeg / 2 - 90; // start at 12 o'clock
        const angleRad = (angleDeg * Math.PI) / 180;
        const cx = wheelSize / 2 + 40 + Math.cos(angleRad) * seatRadius;
        const cy = wheelSize / 2 + 40 + Math.sin(angleRad) * seatRadius;
        return (
          <div
            key={seat.seatId}
            style={{
              position: 'absolute',
              left: cx - 26,
              top: cy - 26,
              width: 52,
              height: 52,
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: '0 0 0 2.5px #060d22, 0 4px 8px rgba(0,0,0,0.4)',
              background: '#0B1838',
            }}
          >
            <SeatAvatar seat={seat} size={52} />
          </div>
        );
      })}
      {/* Centre badge: current sub-spin label or done */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 30%, #FFE48A 0%, #FFC940 50%, #C77A0A 100%)',
          color: '#3B1F00',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontWeight: 800,
          boxShadow: '0 8px 18px rgba(0,0,0,0.55)',
          textShadow: '0 1px 2px rgba(255,255,255,0.45)',
          padding: 4,
        }}
      >
        {currentKind ? (
          <>
            <div style={{ fontSize: 10, opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
              {SUB_SPIN_LABEL[currentKind]}
            </div>
            <div style={{ fontSize: 18 }}>{completedCount + 1}/3</div>
          </>
        ) : (
          <div style={{ fontSize: 13, lineHeight: 1.2, padding: '0 6px' }}>
            Tallying winnings…
          </div>
        )}
      </div>
      {/* Pointer */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 14,
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '14px solid transparent',
          borderRight: '14px solid transparent',
          borderTop: `22px solid ${accent}`,
          filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.55))',
          transition: 'border-top-color 400ms ease-out',
        }}
        aria-hidden
      />
    </div>
  );
}

function PayoutTile({
  seat,
  total,
  highlighted,
}: {
  seat: WheelSeatView;
  total: number;
  highlighted: boolean;
}) {
  const name = useSeatName(seat);
  return (
    <div
      style={{
        padding: '6px 8px',
        borderRadius: 10,
        background: highlighted
          ? 'rgba(255, 201, 64, 0.14)'
          : 'rgba(255,255,255,0.04)',
        border: highlighted
          ? '1px solid rgba(255, 201, 64, 0.5)'
          : '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 88,
      }}
    >
      <div style={{ width: 24, height: 24 }}>
        <SeatAvatar seat={seat} size={24} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.85, lineHeight: 1.1 }}>
          {name.split(' ')[0]}
        </div>
        <div style={{ fontSize: 12, fontWeight: 800, color: total > 0 ? '#FFC940' : 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', gap: 3 }}>
          <Icon name="coin" size={10} alt="" />
          {total}
        </div>
      </div>
    </div>
  );
}

function findSeat(seats: ReadonlyArray<WheelSeatView>, seatId: number): WheelSeatView | null {
  return seats.find((s) => s.seatId === seatId) ?? null;
}
